// ============================================================
// FILE: GardenerProfile.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\GardenerProfile.js
// ============================================================
// PURPOSE:
//   Gardener's own editable profile.
//
// SERVICE AREAS:
//   Dual-list picker (SuburbPicker) — nearby suburbs on the left,
//   selected service areas on the right, arrows to move between.
//   Replaces the old chip cloud, which grew unbounded in dense
//   metro areas. SuburbSearch still sits underneath for adding
//   suburbs outside the 50km radius; those land straight in the
//   selected pane.
//
//   suburbLookup keeps full suburb objects keyed by ID so selected
//   suburbs that aren't in the nearby list still render their name
//   rather than "Suburb #123".
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import client from '../../api/client';
import PortfolioSection from './PortfolioSection';
import CreditBalance from '../../components/CreditBalance';
import SuburbSearch from '../../components/SuburbSearch';
import SuburbPicker from '../../components/SuburbPicker';
import SmsPreferences from '../../components/SmsPreferences';
import MyBookingLink from '../../components/MyBookingLink';
import MyReferrals   from '../../components/MyReferrals';

// Different endpoints spell these fields slightly differently — normalise
// once here so the rest of the component only deals with one shape.
function normalizeSuburb(raw) {
    if (!raw) return null;
    const id = raw.suburb_id ?? raw.id;
    if (id === undefined || id === null) return null;
    return {
        suburb_id:   id,
        suburb_name: raw.suburb_name || raw.name || '',
        postcode:    raw.postcode || raw.post_code || '',
        state_code:  raw.state_code || raw.state || '',
        distance_km: raw.distance_km,
    };
}

export default function GardenerProfile() {
    const [profile,       setProfile]       = useState(null);
    const [skills,        setSkills]        = useState([]);
    const [nearbySuburbs, setNearbySuburbs] = useState([]);
    const [suburbLookup,  setSuburbLookup]  = useState({});  // suburb_id -> full suburb object
    const [searchSuburb,  setSearchSuburb]  = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [saving,        setSaving]        = useState(false);
    const [error,         setError]         = useState('');
    const [success,       setSuccess]       = useState('');

    // IDs we've already tried (and failed) to resolve — stops the
    // resolver effect from retrying forever on a bad ID.
    const resolveAttempted = useRef(new Set());

    const [form, setForm] = useState({
        bio: '', years_experience: '', hourly_rate: '',
        is_available: true, skill_ids: [], suburb_ids: [],
    });

    // Merge suburb objects into the lookup. Never downgrades a known
    // name back to a blank one.
    const rememberSuburbs = (list) => {
        if (!list || list.length === 0) return;
        setSuburbLookup(prev => {
            const next = { ...prev };
            list.forEach(raw => {
                const s = normalizeSuburb(raw);
                if (!s) return;
                if (s.suburb_name || !next[s.suburb_id]) next[s.suburb_id] = s;
            });
            return next;
        });
    };

    // Passed to SuburbPicker so it can label selected suburbs that
    // aren't in the nearby pool.
    const getSuburb = useCallback((id) => suburbLookup[id], [suburbLookup]);

    const loadProfile = async () => {
        const [profileRes, skillsRes] = await Promise.all([
            client.get('/users/profile'),
            client.get('/ref/skills'),
        ]);
        const p = profileRes.data.profile;
        setProfile(p);
        setSkills(skillsRes.data.skills);
        setForm({
            bio:              p.bio || '',
            years_experience: p.years_experience || '',
            hourly_rate:      p.hourly_rate || '',
            is_available:     p.is_available !== false,
            skill_ids:        (p.skills || []).map(s => s.skill_id),
            suburb_ids:       (p.service_areas || []).map(a => a.suburb_id),
        });
        // Saved service areas may already carry names — keep them if so.
        rememberSuburbs(p.service_areas);

        // Load nearby suburbs (50km radius) for the left pane
        if (p.suburb_id) {
            try {
                const r = await client.get(`/ref/suburbs/nearby/${p.suburb_id}`);
                const list = (r.data.suburbs || []).map(normalizeSuburb).filter(Boolean);
                setNearbySuburbs(list);
                rememberSuburbs(list);
            } catch { setNearbySuburbs([]); }
        }
        return p;
    };

    useEffect(() => {
        loadProfile()
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    // Backstop: if a saved service area is outside the 50km radius and the
    // profile payload didn't include its name, resolve it by ID. The nearby
    // endpoint returns the origin suburb itself (distance 0), so we can pull
    // the name out of that. Fails silently — worst case the row keeps its
    // "Suburb #id" label instead of breaking the page.
    useEffect(() => {
        const unresolved = form.suburb_ids.filter(id =>
            !suburbLookup[id]?.suburb_name && !resolveAttempted.current.has(id)
        );
        if (unresolved.length === 0) return;

        let cancelled = false;
        unresolved.forEach(id => resolveAttempted.current.add(id));

        (async () => {
            const found = [];
            for (const id of unresolved) {
                try {
                    const r = await client.get(`/ref/suburbs/nearby/${id}`);
                    const match = (r.data.suburbs || [])
                        .find(s => Number(s.suburb_id) === Number(id));
                    if (match) found.push(match);
                } catch { /* leave the row with its fallback label */ }
            }
            if (!cancelled) rememberSuburbs(found);
        })();

        return () => { cancelled = true; };
    }, [form.suburb_ids, suburbLookup]);

    // Search box adds a suburb straight to the selected pane.
    const handleSearchSelect = (suburb) => {
        if (!suburb) { setSearchSuburb(null); return; }
        const s = normalizeSuburb(suburb);
        if (!s) { setSearchSuburb(null); return; }

        rememberSuburbs([s]);
        setForm(f => f.suburb_ids.includes(s.suburb_id)
            ? f
            : { ...f, suburb_ids: [...f.suburb_ids, s.suburb_id] });
        setSearchSuburb(null);
    };

    const setSuburbIds = (ids) => setForm(f => ({ ...f, suburb_ids: ids }));

    const toggleSkill = (id) => setForm(f => ({
        ...f, skill_ids: f.skill_ids.includes(id)
            ? f.skill_ids.filter(s => s !== id)
            : [...f.skill_ids, id],
    }));

    const handleSave = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            await client.put('/gardeners/profile', {
                bio:              form.bio || null,
                years_experience: form.years_experience ? parseInt(form.years_experience) : null,
                hourly_rate:      form.hourly_rate ? parseFloat(form.hourly_rate) : null,
                is_available:     form.is_available,
            });
            if (form.skill_ids.length > 0)
                await client.put('/gardeners/skills', { skill_ids: form.skill_ids });
            if (form.suburb_ids.length > 0)
                await client.put('/gardeners/areas', { suburb_ids: form.suburb_ids });
            await loadProfile();
            setSuccess('Profile saved.');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save profile.');
        } finally { setSaving(false); }
    };

    if (loading) return <div style={styles.loading}>Loading your profile...</div>;
    if (!profile) return <div style={styles.loading}>{error}</div>;

    const grouped = skills.reduce((acc, s) => {
        if (!acc[s.category]) acc[s.category] = [];
        acc[s.category].push(s);
        return acc;
    }, {});

    return (
        <div style={styles.page}>
            <h1 style={styles.title}>My Profile</h1>

            {/* Credit balance — replaces subscription banner */}
            <div style={styles.creditRow}>
                <CreditBalance compact={true} />
                <a href="/gardener/credits" style={styles.topUpLink}>Top up credits →</a>
            </div>

            {/* Booking link — high on the page deliberately. This is
                the gardener's own customer-facing asset and the thing
                we most want them to actually use; buried below the
                skills editor it would never be found. Renders nothing
                if referral_code is NULL. */}
            <MyBookingLink
                referralCode={profile?.referral_code}
                firstName={profile?.first_name}
            />

            {/* The gardener's other link, deliberately adjacent.
                Both are built from the same code and look nearly
                identical, but /g/CODE brings a CUSTOMER whose job
                only this gardener sees, while /register?ref=CODE
                brings a GARDENER they earn 10% from. Sending the
                wrong one to the wrong person is the obvious
                mistake, and side by side each card says who it is
                for. Compact here; the earnings and the list of
                people referred live on the Credits page. */}
            <MyReferrals compact={true} />

            <div style={styles.tierCard}>
                <div style={styles.tierHeader}>
                    <span style={styles.tierBadge}>
                        {profile.tier_code === 'elite' ? '🏆 ELITE' : profile.tier_code === 'pro' ? '⭐ PRO' : profile.tier_code === 'rising' ? '📈 RISING' : '🌱 STANDARD'}
                    </span>
                    <span style={styles.tierStat}>⭐ {profile.avg_rating ? Number(profile.avg_rating).toFixed(1) : '—'} avg</span>
                    <span style={styles.tierStat}>✅ {profile.total_jobs || 0} jobs</span>
                    <span style={styles.tierStat}>🌟 {profile.five_star_count || 0} five-star</span>
                </div>
                <p style={styles.tierHint}>
                    {profile.tier_code === 'standard' && 'Get 5 five-star reviews to reach Rising tier.'}
                    {profile.tier_code === 'rising'   && 'Get 15 five-star reviews to reach Pro tier.'}
                    {profile.tier_code === 'pro'      && 'Get 30 five-star reviews to reach Elite tier.'}
                    {profile.tier_code === 'elite'    && "You've reached the top tier — nice work!"}
                </p>
            </div>

            {error   && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}

            <div style={styles.card}>
                <h3 style={styles.cardTitle}>About You</h3>
                <div style={styles.field}>
                    <label style={styles.label}>Bio</label>
                    <textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})}
                        style={{...styles.input, height:'90px', resize:'vertical'}}
                        placeholder="Tell buyers about yourself and your experience..." />
                </div>
                <div style={styles.row}>
                    <div style={styles.field}>
                        <label style={styles.label}>Years of experience</label>
                        <input type="number" min="0" value={form.years_experience}
                            onChange={e => setForm({...form, years_experience: e.target.value})} style={styles.input} />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Hourly rate ($)</label>
                        <input type="number" min="0" step="1" value={form.hourly_rate}
                            onChange={e => setForm({...form, hourly_rate: e.target.value})} style={styles.input} />
                    </div>
                </div>
                <div style={styles.field}>
                    <label style={styles.checkboxLabel}>
                        <input type="checkbox" checked={form.is_available}
                            onChange={e => setForm({...form, is_available: e.target.checked})} />
                        {' '}Available for new jobs
                    </label>
                </div>
            </div>

            <div style={styles.card}>
                <h3 style={styles.cardTitle}>Skills</h3>
                {Object.entries(grouped).map(([cat, catSkills]) => (
                    <div key={cat} style={{marginBottom:'12px'}}>
                        <div style={styles.skillCat}>{cat}</div>
                        <div style={styles.chips}>
                            {catSkills.map(s => (
                                <button key={s.skill_id} type="button" onClick={() => toggleSkill(s.skill_id)}
                                    style={form.skill_ids.includes(s.skill_id) ? styles.chipActive : styles.chip}>
                                    {s.skill_name}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.card}>
                <div style={styles.areaHeader}>
                    <h3 style={styles.cardTitle}>Service Areas</h3>
                    <span style={styles.areaCount}>{form.suburb_ids.length} selected</span>
                </div>

                <SuburbPicker
                    available={nearbySuburbs}
                    selectedIds={form.suburb_ids}
                    getSuburb={getSuburb}
                    onChange={setSuburbIds}
                />

                {/* Search adds any suburb outside the 50km pool */}
                <div style={{...styles.skillCat, marginTop:'16px', marginBottom:'6px'}}>
                    Add a suburb outside this area
                </div>
                <SuburbSearch
                    label=""
                    value={searchSuburb}
                    onChange={handleSearchSelect}
                    country={profile?.country || 'AU'}
                    showCountry={false}
                    placeholder="Search any suburb to add..."
                />
            </div>

            <SmsPreferences
                initialOptIn={profile?.sms_opt_in}
                phone={profile?.phone}
            />

            <PortfolioSection />

            <button onClick={handleSave} style={styles.btnSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
            </button>
        </div>
    );
}

const styles = {
    page:          { maxWidth:'700px', margin:'0 auto', padding:'32px 24px' },
    loading:       { textAlign:'center', padding:'60px', color:'#666' },
    title:         { color:'#2d6a4f', marginBottom:'20px' },
    creditRow:     { position:'relative', marginBottom:'16px' },
    topUpLink:     { position:'absolute', top:'50%', right:'0', transform:'translateY(-50%)', color:'#2d6a4f', fontWeight:600, fontSize:'0.85rem', textDecoration:'none' },
    activeBanner:  { backgroundColor:'#e8f5e9', border:'1px solid #66bb6a', borderRadius:'8px', padding:'14px 18px', marginBottom:'16px', fontSize:'0.9rem', color:'#2d6a4f' },
    tierCard:      { background:'white', borderRadius:'10px', padding:'18px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'20px' },
    tierHeader:    { display:'flex', gap:'14px', flexWrap:'wrap', alignItems:'center', marginBottom:'8px' },
    tierBadge:     { fontWeight:'bold', fontSize:'0.95rem', color:'#2d6a4f' },
    tierStat:      { fontSize:'0.85rem', color:'#555' },
    tierHint:      { fontSize:'0.82rem', color:'#888', margin:0 },
    error:         { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px', padding:'10px 14px', color:'#c0392b', marginBottom:'16px', fontSize:'0.88rem' },
    success:       { backgroundColor:'#e8f5e9', border:'1px solid #66bb6a', borderRadius:'6px', padding:'10px 14px', color:'#2d6a4f', marginBottom:'16px', fontSize:'0.88rem' },
    card:          { background:'white', borderRadius:'10px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'16px' },
    cardTitle:     { fontSize:'1rem', fontWeight:'bold', color:'#2d6a4f', marginBottom:'12px', margin:'0 0 12px' },
    field:         { marginBottom:'16px' },
    row:           { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
    label:         { display:'block', fontWeight:600, marginBottom:'6px', color:'#333', fontSize:'0.88rem' },
    checkboxLabel: { fontSize:'0.9rem', color:'#333', display:'flex', alignItems:'center', gap:'6px' },
    input:         { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'0.95rem', boxSizing:'border-box' },
    skillCat:      { fontSize:'0.78rem', fontWeight:'bold', color:'#888', textTransform:'uppercase', marginBottom:'6px' },
    chips:         { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:          { padding:'5px 12px', borderRadius:'16px', border:'1px solid #ddd', background:'white', cursor:'pointer', fontSize:'0.85rem' },
    chipActive:    { padding:'5px 12px', borderRadius:'16px', border:'1px solid #2d6a4f', background:'#e8f5e9', color:'#2d6a4f', cursor:'pointer', fontSize:'0.85rem', fontWeight:'bold' },
    areaHeader:    { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' },
    areaCount:     { fontSize:'0.8rem', color:'#888', fontWeight:500 },
    btnSave:       { width:'100%', padding:'14px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer', marginTop:'8px' },
};
